import { IonButton, IonButtons, IonContent, IonHeader, IonModal, IonToolbar } from '@ionic/react';
import { useMemo, useState } from 'react';
import { addDays, type IsoDate } from '../../domain/dates';
import type { PlannedDay } from '../../domain/plan';
import type { Recipe } from '../../domain/recipe';
import { formatDate, formatPrice, recipeName } from '../i18n/format';
import { useMessages } from '../i18n/LanguageProvider';
import { FoodPlate } from '../pictures/FoodPlate';
import { recipePicture } from '../pictures/pictures';
import { addOnText } from '../recipes/add-on-text';
import { RecipeDetails } from '../recipes/RecipeDetails';
import { recipeById } from '../recipes/recipe-lookup';
import { useAppData } from '../state/AppDataProvider';
import { currentPlan } from '../state/plan-service';
import { usePlanEdits } from '../state/use-plan-edits';
import { AlternativesList, QuickSwap } from '../today/NoEnergySheet';
import { replaceChoices } from './plan-view';

function ReplaceList({ date, onDone }: { date: IsoDate; onDone: () => void }) {
  const { data, today } = useAppData();
  const plans = usePlanEdits();
  const { language, t } = useMessages();
  const choices = useMemo(() => replaceChoices(data, today, date), [data, today, date]);
  const portions = new Map(choices.map((choice) => [choice.recipe.id, choice.portion]));
  const choose = (recipeId: string) => {
    void plans.edit({ kind: 'replace', date, recipeId }).finally(onDone);
  };
  const price = (recipe: Recipe) =>
    t.perPortion(formatPrice(portions.get(recipe.id) ?? 0, language, true));
  return (
    <>
      <p className="app-lead">{t.replaceTitle}</p>
      {choices.length === 0 ? (
        <p className="app-muted">{t.noReplacement}</p>
      ) : (
        <AlternativesList
          recipes={choices.map((choice) => choice.recipe)}
          onChoose={choose}
          detail={price}
        />
      )}
    </>
  );
}

function LowEnergyButton({ date }: { date: IsoDate }) {
  const { data, dispatch } = useAppData();
  const plans = usePlanEdits();
  const { t } = useMessages();
  const [busy, setBusy] = useState(false);
  if (data.household.lowEnergyDates.includes(date)) {
    return <p className="app-muted">{t.lowEnergyMarked}</p>;
  }
  if (busy) return <p className="app-muted">{t.planning}</p>;
  const mark = () => {
    setBusy(true);
    void plans
      .edit({ kind: 'low-energy', date })
      .then(() => dispatch({ type: 'add-low-energy-date', date }))
      .finally(() => setBusy(false));
  };
  return (
    <IonButton fill="clear" onClick={mark}>
      {t.lowEnergyDay}
    </IonButton>
  );
}

function DayActions({ day }: { day: PlannedDay }) {
  const { today } = useAppData();
  const plans = usePlanEdits();
  const { t } = useMessages();
  const [mode, setMode] = useState<'buttons' | 'replace' | 'no-energy'>('buttons');
  const [notice, setNotice] = useState<string>();
  const { date, dinner } = day;
  if (mode === 'replace') return <ReplaceList date={date} onDone={() => setMode('buttons')} />;
  if (mode === 'no-energy') {
    return (
      <QuickSwap
        onDone={(text) => {
          setNotice(text);
          setMode('buttons');
        }}
      />
    );
  }
  const lock = () => void plans.edit({ kind: 'lock', date, locked: !dinner.locked });
  return (
    <>
      {notice && <p className="app-hint">{notice}</p>}
      <div className="app-actions">
        <IonButton onClick={() => setMode('replace')}>{t.replace}</IonButton>
        <IonButton fill="outline" onClick={lock}>
          {dinner.locked ? t.unlock : t.lock}
        </IonButton>
        {date === today && (
          <IonButton fill="outline" onClick={() => setMode('no-energy')}>
            {t.noEnergy}
          </IonButton>
        )}
      </div>
      <LowEnergyButton date={date} />
    </>
  );
}

function Meal({ recipe }: { recipe: Recipe }) {
  const { language } = useMessages();
  return (
    <dd className="app-meal">
      <FoodPlate picture={recipePicture(recipe.id)} size={32} />
      {recipeName(recipe, language)}
    </dd>
  );
}

function DayContent({ date }: { date: IsoDate }) {
  const { data, today } = useAppData();
  const { language, t } = useMessages();
  const [showRecipe, setShowRecipe] = useState(false);
  const days = data.plans.flatMap((stored) => stored.plan.days);
  const day = days.find((planned) => planned.date === date);
  if (!day) return null;
  const running = currentPlan(data, today)?.plan.days.some((planned) => planned.date === date);
  const previous = days.find((planned) => planned.date === addDays(date, -1));
  const lunch = previous && recipeById(previous.dinner.recipeId);
  const dinner = recipeById(day.dinner.recipeId);
  const title = formatDate(date, language, 'weekday-long');
  return (
    <article aria-label={title}>
      <p className="app-lead">{title}</p>
      <dl className="app-meals">
        <dt>{t.breakfast}</dt>
        <Meal recipe={recipeById(day.breakfast.recipeId)} />
        {lunch?.lunchboxFriendly && <dt>{t.lunch}</dt>}
        {lunch?.lunchboxFriendly && <Meal recipe={lunch} />}
        <dt>{t.dinnerTitle}</dt>
        <Meal recipe={dinner} />
      </dl>
      <ul className="app-chips">
        {day.dinner.meatAddOns.map((addOn) => (
          <li key={`${addOn.memberId}-${addOn.productId}`} className="app-chip app-chip-partner">
            {addOnText(addOn, data.household.members, language)}
          </li>
        ))}
      </ul>
      <div className="app-actions">
        <IonButton fill="outline" onClick={() => setShowRecipe(!showRecipe)}>
          {t.recipe}
        </IonButton>
      </div>
      {showRecipe && <RecipeDetails recipe={dinner} servings={day.dinner.servings} />}
      {running && date >= today && <DayActions day={day} />}
    </article>
  );
}

/**
 * DE: Ein Tag im Detail mit Änderungen; vergangene Tage und der nächste Plan nur zum Ansehen.
 * EN: One day in detail with changes; past days and the next plan are view-only.
 */
export function DayDetail({ date, onClose }: { date: IsoDate | undefined; onClose: () => void }) {
  const { t } = useMessages();
  return (
    <IonModal isOpen={date !== undefined} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>{t.close}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">{date && <DayContent date={date} />}</IonContent>
    </IonModal>
  );
}
